import { useEffect, useMemo, useState } from 'react';
import type { MouseEvent } from 'react';

import dynamic from 'next/dynamic';

import { useMediaQuery } from '@react-hookz/web';
import classNames from 'classnames';
import { differenceInHours } from 'date-fns';
import { Link, NavLink, useHistory } from 'react-router-dom';
import { css, useTheme } from 'styled-components';

import { AnalyticSources, FlowTriggered, UNIFIED } from 'src/app/analytics/analyticsEvents';
import { HomeBanner, RefigerationBanner } from 'src/app/components/Banner/Banners';
import BranchSmartBanner from 'src/app/components/Branch/SmartBanner/container';
import { OverrideContentSecurityPolicy } from 'src/app/components/CSP';
import CapsuleIcon from 'src/app/components/Elements/CapsuleIcon';
import { DevOnly } from 'src/app/components/Elements/DevOnly';
import { Divider } from 'src/app/components/Elements/Divider';
import SideBar, { sideBarStyles } from 'src/app/components/Elements/SideBar';
import SideMenu, { SideMenuComponents } from 'src/app/components/Elements/SideMenu';
import { AppDownloadInterstitial } from 'src/app/components/Pages/AppDownload';
import { useGetHomeBanner } from 'src/app/hooks/data/homeBanner';
import { useOrderCount } from 'src/app/hooks/data/order';
import { useOTCMedCategoriesV2 } from 'src/app/hooks/data/otc';
import { useUser } from 'src/app/hooks/data/user';
import { usePOTSExperimentInterstitial } from 'src/app/hooks/feature_data/usePOTSExperiment';
import useAuth from 'src/app/hooks/useAuth';
import useExperiments from 'src/app/hooks/useExperiments';
import { useSegmentEvent } from 'src/app/hooks/useSegmentEvent';
import { LayoutRoutes } from 'src/app/popsicle/router/layoutRoutes';
import {
  accountPage,
  archivedPrescriptions,
  contact,
  employeeBenefitsPage,
  loggedInQuestions,
  loggedInTransferAppInterstitialPrompt,
  loggedInTransferRx,
  logout,
  medications,
  orders,
  shop2,
  shop2Category,
  showYourDoctor,
} from 'src/app/router/paths';
import { loadAccount, loadFacility, loadPatient, segmentTrack } from 'src/app/state/actions';
import * as actionTypes from 'src/app/state/actions/types';
import { getCurrentPathname, getPrimaryPatientId } from 'src/app/state/selectors';
import { useDispatch, useSelector } from 'src/app/state/store';
import { RX_TAB_ALERT_BANNER_DISMISSED } from 'src/app/storage/cookieKeys';
import { setCookie, getCookie } from 'src/app/storage/cookies';
import { LAST_SHOWN_APP_DOWNLOAD_INTERSTITIAL } from 'src/app/storage/localStorageKeys';
import { VisuallyHiddenElement } from 'src/app/styles/utils';
import { CAPSULE_ICON_STRINGS, CTATypes, VERSION } from 'src/consts';
import { useLogRocketInit } from 'src/hooks/useLogRocketInit';

import { MobileHeader } from './components/MobileHeader';
import type { LoggedInProps } from './container';
import { NewArchivedMedicationPageModal } from './NewArchivedMedicationPageModal';
import { NavLinkOverlay, PositionedBottomCenter } from './styles';
import styles from './styles.module.scss';
import { TabbedFooterBar } from './TabbedFooterBar';

const displayName = 'LoggedIn';

const mainContentId = 'main-content';
const chatContentId = 'chat-content';

const CapsuleChat = dynamic(() => import('src/app/components/CapsuleChat').then(module => module.CapsuleChat), {
  ssr: false,
});

export const LoggedInRenderer = ({
  currentPath,
  dispatchTransferRxButtonClick,
  hasFixedNav,
  hideNavBar,
  hideNavLinks,
  inOrderTracking,
  isInAddInsurance,
  isFauxLoggedInLayout,
  isFullWidthLoggedInLayout,
  isInMedicationsPath,
  isInShopPath, // TODO delete
  isInEssentialsPath, // New shop
  isInEssentialsCategoryPath,
  isLoggedInTransferPath,
  isSimpleNav,
  isInWelcomePath,
  isInPartnerOnboardingPath,
  isInHealthProfilePath,
  isUserPartOfCapsuleAtWork,
  location,
  refrigerationMessage,
  showTabbedFooterBar,
  dispatchGetOrderHistory,
  dispatchSetNewArchivedPageModalShown,
  dispatchSetShowNewArchivedPageModal,
  dispatchSetPageAlert,
  isNewArchivedPageModalShown,
  isMinimalStyleLayout,
  isUpsellEssentialsRoute,
}: LoggedInProps) => {
  const trackSelectEssentialsCategoryEvent = useSegmentEvent('selectEssentialsCategory', { shouldTrackOnMount: false });
  const theme = useTheme();
  const { push } = useHistory();
  const isSmallDown = useMediaQuery(theme.breakpoints.smallDown);
  const isMediumDown = useMediaQuery(theme.breakpoints.mediumDown);

  const dispatch = useDispatch();
  const pathname = useSelector(getCurrentPathname);
  const primary_patient_id = useSelector(getPrimaryPatientId);

  const [hasAlertBannerBeenDismissed, setHasAlertBannerBeenDismissed] = useState(
    typeof window !== 'undefined' ? getCookie(RX_TAB_ALERT_BANNER_DISMISSED) === 'true' : false
  );

  const { loggedInUser, isLoggedIn } = useAuth();
  const shouldRenderChat = isLoggedIn && !isInWelcomePath && !isInPartnerOnboardingPath;
  const { data: user, isSuccess: isLoadUserSuccess } = useUser(
    loggedInUser ? loggedInUser.uuid : '',
    Boolean(loggedInUser),
    3
  );

  const { data: allOtcCategories } = useOTCMedCategoriesV2({ enabled: pathname.startsWith(shop2.toPath()) });

  useEffect(() => {
    const loadData = async () => {
      if (isLoadUserSuccess && user) {
        dispatch(loadAccount(user.accountId));
        dispatch(loadPatient(user.accountId)); // TODO: after removing dependency on redux patients, start fetching patients right in the component with react query.
        dispatch(loadFacility(user.accountId));
        dispatch({
          type: actionTypes.SEGMENT_IDENTIFY,
          accountId: user.accountId,
        });

        if (primary_patient_id) {
          //LogRocket.identify(user.accountId, { primary_patient_id });
        } else {
          //LogRocket.identify(user.accountId);
        }
      }
    };

    loadData();
  }, [user, isLoadUserSuccess, dispatch, primary_patient_id]);

  const [isMenuShown, setIsMenuShown] = useState(false);

  const dispatchViewBannerSegmentEvent = useSegmentEvent('viewBanner', { shouldTrackOnMount: false });
  const dispatchSelectBannerCTASegmentEvent = useSegmentEvent('selectBannerCTA', { shouldTrackOnMount: false });
  const { data: homeBanner } = useGetHomeBanner();

  const showHomeBanner = isInMedicationsPath && homeBanner;
  const ctaType = homeBanner?.cta?.action?.type;
  const chatType = homeBanner?.cta?.action?.metadata?.chat_type;

  useEffect(() => {
    if (showHomeBanner) {
      if (
        ctaType &&
        [CTATypes.rescheduleOrder, CTATypes.openChat, CTATypes.rescheduleOrderInvalidAddress].includes(ctaType)
      ) {
        let flow_triggered = FlowTriggered.selfServiceReschedule;

        if (ctaType === CTATypes.rescheduleOrderInvalidAddress) {
          flow_triggered = FlowTriggered.failedDelivery;
        }
        if (ctaType === CTATypes.rescheduleOrder) {
          flow_triggered = FlowTriggered.chatReschedule;
        }
        if (ctaType === CTATypes.rescheduleOrderInvalidAddress) {
          flow_triggered = FlowTriggered.failedDelivery;
        }

        dispatchViewBannerSegmentEvent({
          banner_action: AnalyticSources.rescheduleDelivery,
          banner_location: 'top',
          banner_page: AnalyticSources.yourPrescriptions,
          flow_triggered,
        });
      }
    }
  }, [dispatchViewBannerSegmentEvent, showHomeBanner, ctaType, chatType]);

  useEffect(() => {
    if (isInMedicationsPath) {
      dispatchGetOrderHistory();
    }
  }, [dispatchGetOrderHistory, isInMedicationsPath]);

  const handleToggleMenu = (e: MouseEvent) => {
    e.preventDefault();
    setIsMenuShown(!isMenuShown);

    dispatch(segmentTrack(UNIFIED.viewNavbar, { pagepath: pathname, is_logged_in: true }));
  };
  const handleCloseMenu = () => {
    setIsMenuShown(false);
  };

  const handleNewArchivedModalClose = async () => {
    dispatchSetNewArchivedPageModalShown();
    // We only want to show the callout if has not been seen
    dispatchSetShowNewArchivedPageModal(false);
  };

  const handleOnBannerCallToActionClicked = () => {
    if (homeBanner?.showDismissOption) {
      setCookie(RX_TAB_ALERT_BANNER_DISMISSED, 'true', 7);
      setHasAlertBannerBeenDismissed(true);
    } else if (ctaType === CTATypes.rescheduleOrder) {
      dispatchSelectBannerCTASegmentEvent({
        banner_action: AnalyticSources.rescheduleDelivery,
        banner_location: 'top',
        banner_page: AnalyticSources.yourPrescriptions,
        flow_triggered: FlowTriggered.selfServiceReschedule,
      });
      dispatchSetPageAlert(null);
    } else if (ctaType === CTATypes.openChat) {
      dispatchSelectBannerCTASegmentEvent({
        banner_action: AnalyticSources.rescheduleDelivery,
        banner_location: 'top',
        banner_page: AnalyticSources.yourPrescriptions,
        flow_triggered: FlowTriggered.chatReschedule,
      });
    } else if (ctaType === CTATypes.rescheduleOrderInvalidAddress) {
      dispatchSelectBannerCTASegmentEvent({
        banner_action: AnalyticSources.rescheduleDeliveryFailedAddress,
        banner_location: 'top',
        banner_page: AnalyticSources.yourPrescriptions,
        flow_triggered: FlowTriggered.failedDelivery,
      });
    }
  };

  const lastShownAppDownloadInterstitial = window.localStorage.getItem(LAST_SHOWN_APP_DOWNLOAD_INTERSTITIAL);

  const hasAppDownloadInterstitialBeenShownInTheLast24Hrs =
    useMemo(() => {
      return lastShownAppDownloadInterstitial
        ? differenceInHours(new Date(), new Date(lastShownAppDownloadInterstitial)) < 24.0
        : false;
    }, [lastShownAppDownloadInterstitial]) || false;

  const [hasDismissedAppDownload, setHasDismissedAppDownload] = useState(false);

  const handleOnCloseClicked = () => {
    setHasDismissedAppDownload(true);
  };

  const { data: orderCount, isFetched: hasFetchedOrderCount } = useOrderCount();
  const [isUserEnabledForAppAdoptionExperiment, setIsUserEnabledForAppAdoptionExperiment] = useState<boolean>(false);
  const [isAppAdoptionExperimentLoading, setIsAppAdoptionExperimentLoading] = useState<boolean>(true);
  const { fetchVariant } = useExperiments();
  useEffect(() => {
    if (!!user) {
      const evaluateAppAdoptionExperiment = async () => {
        const appAdoptionExperimentResult = await fetchVariant({
          experimentKey: 'app_adoption',
          userId: user ? user.accountId : '',
        });

        setIsUserEnabledForAppAdoptionExperiment(appAdoptionExperimentResult === 'variation_1');
        setIsAppAdoptionExperimentLoading(false);
      };
      evaluateAppAdoptionExperiment();
    }
  }, [fetchVariant, user]);

  const hasCompletedOrders = hasFetchedOrderCount && typeof orderCount === 'number' && orderCount > 0;

  const shouldShowAppDownloadInterstitial = !!(
    !isAppAdoptionExperimentLoading &&
    isSmallDown &&
    isUserEnabledForAppAdoptionExperiment &&
    hasCompletedOrders &&
    !hasAppDownloadInterstitialBeenShownInTheLast24Hrs &&
    !hasDismissedAppDownload &&
    isInMedicationsPath
  );

  const {
    shouldShow: shouldShowPOTSInterstitial,
    variant: variantPOTSExperiment,
    isLoading: isLoadingPOTSExperiment,
  } = usePOTSExperimentInterstitial();

  useLogRocketInit({ condition: !isLoadingPOTSExperiment && !!variantPOTSExperiment });

  useEffect(() => {
    if (shouldShowPOTSInterstitial) {
      push(loggedInTransferAppInterstitialPrompt.route);
    }
  }, [push, shouldShowPOTSInterstitial]);

  if (shouldShowAppDownloadInterstitial) {
    return <AppDownloadInterstitial onCloseClicked={handleOnCloseClicked} />;
  }

  const shouldShowSmartBranchBanner =
    !shouldShowPOTSInterstitial && !shouldShowAppDownloadInterstitial && isInMedicationsPath && !isInHealthProfilePath;

  return (
    <div
      className={classNames(styles.component, isFauxLoggedInLayout ? 'layout-faux-logged-in' : 'layout-logged-in', {
        marketing: isFauxLoggedInLayout && !isMinimalStyleLayout,
        'logged-in': !isFauxLoggedInLayout && !isMinimalStyleLayout,
      })}
    >
      <OverrideContentSecurityPolicy />

      {/**
       * Skip Link Menu
       * @see https://css-tricks.com/how-to-create-a-skip-to-content-link/
       */}
      {isLoggedIn && (
        <VisuallyHiddenElement id="skip-link-menu">
          <a href={`#${mainContentId}`}>Skip to content</a>
          <a href={`#${chatContentId}`}>Skip to chat</a>
        </VisuallyHiddenElement>
      )}

      <MobileHeader
        isInAddInsurance={isInAddInsurance}
        isFauxLoggedInLayout={isFauxLoggedInLayout || isMinimalStyleLayout}
        hasFixedNav={hasFixedNav}
        hideNavBar={hideNavBar}
        showMenu={isMenuShown}
        handleToggleMenu={handleToggleMenu}
      />
      <div
        role="navigation"
        aria-label="Main Desktop Navigation Panel"
        className={classNames(styles.header, styles.desktop, {
          [styles.simpleNav]: isSimpleNav,
          [styles.displayNone]: hideNavBar,
          [styles.fauxLoggedIn]: isFauxLoggedInLayout,
          [styles.fullWidthLoggedIn]: isFullWidthLoggedInLayout,
        })}
      >
        <div className={styles.leftHeaderSection}>
          <div className={styles.logo}>
            <Link aria-label="Capsule Home" data-cy="logo" to={medications.toPath()}>
              <CapsuleIcon iconString={CAPSULE_ICON_STRINGS.capsule_logo} block />
            </Link>
          </div>
          {!hideNavLinks && (
            <>
              <NavLink className={styles.headerLink} to={medications.toPath()}>
                Home
              </NavLink>
              <NavLink
                className={styles.headerLink}
                to={loggedInTransferRx.toPath()}
                onClick={dispatchTransferRxButtonClick}
              >
                Transfer Rx
              </NavLink>
              <NavLink className={styles.headerLink} to={contact.toPath()}>
                Contact us
              </NavLink>
              <NavLink className={styles.headerLink} to={loggedInQuestions.toPath()}>
                Questions
              </NavLink>
              {isUserPartOfCapsuleAtWork && (
                <NavLink className={styles.headerLink} to={employeeBenefitsPage.toPath()}>
                  Employee benefits
                </NavLink>
              )}
            </>
          )}
        </div>
        <NavLink className={styles.headerLink} to={`${logout.toPath()}?initiated=true`}>
          Sign out
        </NavLink>
      </div>
      <>
        {shouldShowSmartBranchBanner && <BranchSmartBanner />}

        {/* New flow shows the refrigeration banner in order tracking, if there is no refrigeration message, it is null */}
        {inOrderTracking && refrigerationMessage && <RefigerationBanner titleText={refrigerationMessage} />}

        {showHomeBanner && (
          <HomeBanner
            homeBanner={homeBanner}
            hasAlertBannerBeenDismissed={hasAlertBannerBeenDismissed}
            onCallToActionClick={handleOnBannerCallToActionClicked}
          />
        )}
      </>
      <SideMenu
        isShown={isMenuShown}
        onClose={handleCloseMenu}
        className={styles.menu}
        hasGreenBackground
        isHiddenOnLargeScreens
      >
        <SideMenuComponents.DecoratedLink
          to={medications.toPath()}
          onClick={handleCloseMenu}
          activeClassName={'active'}
        >
          Prescriptions
        </SideMenuComponents.DecoratedLink>
        <SideMenuComponents.DecoratedLink
          className={styles.shopPage}
          to={shop2.toPath()}
          onClick={handleCloseMenu}
          activeClassName={'active'}
        >
          Essentials
        </SideMenuComponents.DecoratedLink>

        <Divider width={'20.5'} backgroundColor="capsuleGreen60" margin={theme.dividerMargins.leftIndent} />
        {user?.accountId && (
          <SideMenuComponents.DecoratedLink
            to={accountPage.toPath()}
            onClick={handleCloseMenu}
            activeClassName={'active'}
          >
            Your account
          </SideMenuComponents.DecoratedLink>
        )}
        <SideMenuComponents.DecoratedLink to={orders.toPath()} onClick={handleCloseMenu} activeClassName={'active'}>
          Your orders
        </SideMenuComponents.DecoratedLink>
        <SideMenuComponents.DecoratedLink
          to={archivedPrescriptions.toPath()}
          onClick={handleCloseMenu}
          activeClassName={'active'}
        >
          Archived prescriptions
        </SideMenuComponents.DecoratedLink>
        <SideMenuComponents.DecoratedLink to={contact.toPath()} onClick={handleCloseMenu} activeClassName={'active'}>
          Contact us
        </SideMenuComponents.DecoratedLink>
        <SideMenuComponents.DecoratedLink
          to={loggedInQuestions.toPath()}
          onClick={handleCloseMenu}
          activeClassName={'active'}
        >
          Questions
        </SideMenuComponents.DecoratedLink>

        <Divider width={'20.5'} backgroundColor="capsuleGreen60" margin={theme.dividerMargins.leftIndent} />

        <SideMenuComponents.DecoratedLink
          to={showYourDoctor.toPath()}
          onClick={handleCloseMenu}
          activeClassName={'active'}
        >
          Show your doctor
        </SideMenuComponents.DecoratedLink>

        <SideMenuComponents.DecoratedLink
          to={`${logout.toPath()}?initiated=true`}
          onClick={handleCloseMenu}
          activeClassName={'active'}
        >
          Sign out
        </SideMenuComponents.DecoratedLink>

        <PositionedBottomCenter as={DevOnly}>
          <div className={styles.buildNumber}>build {VERSION}</div>
        </PositionedBottomCenter>
      </SideMenu>
      <div className={styles.contentRow}>
        {!isFauxLoggedInLayout && !isLoggedInTransferPath && !isMinimalStyleLayout && (
          <SideBar>
            <NavLink className={sideBarStyles.link} to={medications.toPath()}>
              Prescriptions
            </NavLink>

            <NavLinkOverlay
              className={sideBarStyles.link}
              to={`${shop2.toPath()}`}
              exact={true}
              isActive={(_, location) => {
                const params = new URLSearchParams(location.search);
                const categoryQs = decodeURIComponent(params.get('category') || '');
                return location.pathname === shop2.toPath() && categoryQs === '';
              }}
            >
              Essentials
            </NavLinkOverlay>

            {pathname.startsWith(shop2.toPath()) && (
              <>
                {allOtcCategories
                  ?.filter(c => c.items.length > 0)
                  .map(c => {
                    return (
                      <NavLink
                        className={sideBarStyles.secondaryLink}
                        to={`${shop2Category.toPath()}?category=${encodeURIComponent(c.name)}`}
                        exact={true}
                        isActive={(_, location) => {
                          return (
                            location.pathname === shop2Category.toPath() &&
                            location.search.includes(`category=${encodeURIComponent(c.name)}`)
                          );
                        }}
                        onClick={() => trackSelectEssentialsCategoryEvent({ aisle: c.name, source: 'category_tab' })}
                      >
                        {c.name}
                      </NavLink>
                    );
                  })}
              </>
            )}

            <Divider width={'21'} backgroundColor="capsuleGreen60" margin={theme.dividerMargins.leftIndent} />
            {user?.accountId && (
              <NavLink className={sideBarStyles.link} to={accountPage.toPath()}>
                Your account
              </NavLink>
            )}
            <NavLink className={sideBarStyles.link} to={orders.toPath()}>
              Your orders
            </NavLink>

            <NavLinkOverlay
              onClick={() => handleNewArchivedModalClose()}
              className={sideBarStyles.link}
              to={archivedPrescriptions.toPath()}
            >
              <>
                <NewArchivedMedicationPageModal
                  handleModalClose={handleNewArchivedModalClose}
                  isActive={isNewArchivedPageModalShown}
                />
              </>
              Archived prescriptions
            </NavLinkOverlay>

            <DevOnly
              css={css`
                margin-top: ${({ theme }) => theme.space.s5};
                margin-left: auto;
                width: calc(100% - ${({ theme }) => theme.space.s5});
                & > header {
                  top: -1.1rem;
                  background-color: ${({ theme }) => theme.colors.capsuleGreen00};
                }
              `}
            >
              Account ID: {user?.accountId}
            </DevOnly>

            <PositionedBottomCenter as={DevOnly}>
              <div className={styles.buildNumber}>build {VERSION}</div>
            </PositionedBottomCenter>
          </SideBar>
        )}

        <div
          className={classNames(
            (isInShopPath && isMediumDown) ||
              isInEssentialsPath ||
              isInEssentialsCategoryPath ||
              isUpsellEssentialsRoute ||
              isInAddInsurance
              ? styles.noPaddingContentContainer
              : styles.contentContainer,
            isFauxLoggedInLayout ? styles.fauxLoggedIn : undefined,
            isFullWidthLoggedInLayout ? styles.fullWidthLoggedIn : undefined
          )}
        >
          <div
            className={classNames(
              styles.content,
              isFauxLoggedInLayout ? styles.fauxLoggedIn : undefined,
              isFullWidthLoggedInLayout ? styles.fullWidthLoggedIn : undefined,
              showTabbedFooterBar ? styles.tabbedFooterContent : undefined
            )}
            id={mainContentId}
          >
            <LayoutRoutes renderKey="content" displayName={displayName} location={location} />
          </div>
        </div>
      </div>

      {shouldRenderChat && (
        <div id={chatContentId}>
          <CapsuleChat />
        </div>
      )}

      {showTabbedFooterBar && <TabbedFooterBar currentPath={currentPath} />}
    </div>
  );
};

LoggedInRenderer.displayName = displayName;
